"use client";

import { useAuth } from "@/lib/auth-context";
import { useEffect, useState, useCallback } from "react";
interface Insight {
  insightId: string;
  category: string;
  type: string;
  severity: string;
  title: string;
  body: string;
  createdAt: string;
}
const SEVERITY_STYLES: Record<string, {
  bg: string;
  text: string;
}> = {
  critical: {
    bg: "bg-error/10 border-error/20",
    text: "text-error"
  },
  high: {
    bg: "bg-warning/10 border-warning/20",
    text: "text-warning"
  },
  medium: {
    bg: "bg-info/10 border-info/20",
    text: "text-info"
  },
  low: {
    bg: "bg-base-200/50 border-white/5",
    text: "text-base-content/60"
  },
  info: {
    bg: "bg-base-200/30 border-white/5",
    text: "text-base-content/40"
  }
};
const TYPE_ICONS: Record<string, string> = {
  anomaly: "⚡",
  pattern: "📊",
  suggestion: "💡",
  warning: "⚠️",
  health: "✓"
};
const CATEGORY_CONFIG: Record<string, {
  icon: string;
  label: string;
  color: string;
}> = {
  security: {
    icon: "🛡️",
    label: "Security",
    color: "text-error"
  },
  fraud: {
    icon: "🚨",
    label: "Fraud Detection",
    color: "text-error"
  },
  alerting: {
    icon: "🔔",
    label: "Alerting",
    color: "text-warning"
  },
  compliance: {
    icon: "📜",
    label: "Compliance",
    color: "text-warning"
  },
  performance: {
    icon: "⚡",
    label: "Performance",
    color: "text-info"
  },
  reliability: {
    icon: "🔗",
    label: "Reliability",
    color: "text-info"
  },
  data: {
    icon: "📈",
    label: "Data & Activity",
    color: "text-primary"
  },
  capacity: {
    icon: "📦",
    label: "Capacity & Usage",
    color: "text-primary"
  },
  notifications: {
    icon: "📬",
    label: "Notifications",
    color: "text-secondary"
  },
  services: {
    icon: "🌐",
    label: "Service Health",
    color: "text-secondary"
  },
  general: {
    icon: "📋",
    label: "General Health",
    color: "text-success"
  }
};
function timeAgo(date: string): string {
  const d = new Date(date.endsWith("Z") || date.includes("+") ? date : date + "Z");
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}
interface TokenUsage {
  used: number;
  limit: number;
  enabled: boolean;
  month: string;
  pct: number;
}
export function InsightsCard() {
  const {
    fetchWithAuth,
    tenant
  } = useAuth();
  const [insights, setInsights] = useState<Insight[]>([]);
  const [usage, setUsage] = useState<TokenUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const canManage = tenant?.role === "owner" || tenant?.role === "admin";
  const load = useCallback(async () => {
    const [insRes, usageRes] = await Promise.all([fetchWithAuth("/platform/insights?limit=30"), fetchWithAuth("/platform/insights/usage")]);
    if (insRes.ok) setInsights(await insRes.json());
    if (usageRes.ok) {
      const u = await usageRes.json();
      setUsage(u);
      if (u.limit > 0 && u.used >= u.limit) setLimitReached(true);
    }
    setLoading(false);
  }, [fetchWithAuth]);
  useEffect(() => {
    load();
  }, [load]);
  const generate = async () => {
    setGenerating(true);
    const res = await fetchWithAuth("/platform/insights/generate", {
      method: "POST"
    });
    if (res.ok) {
      const result = await res.json();
      if (result.reason === "token_limit_reached") {
        setLimitReached(true);
        if (usage) setUsage({
          ...usage,
          used: result.used ?? usage.used,
          pct: usage.limit > 0 ? Math.round((result.used ?? usage.used) / usage.limit * 100) : 0
        });
      } else if (result.reason === "plan_not_eligible") {
        alert("AI Insights is available on Pro plans and above.");
      } else if (result.reason === "not_configured") {
        alert("AI Insights is not configured on this instance.");
      } else {
        load();
      }
    }
    setGenerating(false);
  };
  const dismiss = async (insightId: string) => {
    await fetchWithAuth(`/platform/insights/${insightId}/dismiss`, {
      method: "POST"
    });
    setInsights(prev => prev.filter(i => i.insightId !== insightId));
  };
  if (loading) return <div className="rounded-xl border border-white/5 bg-base-100 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-lg bg-base-200 animate-pulse" />
        <div><div className="h-4 w-24 rounded bg-base-200 animate-pulse" /><div className="h-3 w-40 rounded bg-base-200 animate-pulse mt-1" /></div>
      </div>
      <div className="space-y-2"><div className="h-12 rounded-lg bg-base-200/30 animate-pulse" /><div className="h-12 rounded-lg bg-base-200/30 animate-pulse" /></div>
    </div>;

  // Group by category
  const grouped: Record<string, Insight[]> = {};
  for (const insight of insights) {
    const cat = insight.category || "general";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(insight);
  }

  // Sort categories: ones with high severity first
  const severityOrder: Record<string, number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
    info: 4
  };
  const sortedCategories = Object.keys(grouped).sort((a, b) => {
    const aMax = Math.min(...grouped[a].map(i_0 => severityOrder[i_0.severity] ?? 4));
    const bMax = Math.min(...grouped[b].map(i_1 => severityOrder[i_1.severity] ?? 4));
    return aMax - bMax;
  });
  const lastGenerated = insights.length > 0 ? timeAgo(insights[0].createdAt) : null;
  return <div className="rounded-xl border border-white/5 bg-base-100 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-white/5 bg-base-200 text-primary">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold">AI Insights</p>
            <p className="text-xs text-base-content/30">
              {lastGenerated ? `Last analysis ${lastGenerated}` : "Automated monitoring analysis"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {usage && usage.limit > 0 && <div className="text-right">
              <div className="flex items-center gap-2">
                <div className="h-1 w-16 overflow-hidden rounded-full bg-white/5">
                  <div className={`h-full rounded-full transition-all ${usage.pct >= 90 ? 'bg-error' : usage.pct >= 70 ? 'bg-warning' : 'bg-primary'}`} style={{
                width: `${Math.min(usage.pct, 100)}%`
              }} />
                </div>
                <span className="text-[10px] text-base-content/20">{usage.pct}%</span>
              </div>
              <p className="text-[9px] text-base-content/15 mt-0.5">{usage.used.toLocaleString()} / {usage.limit.toLocaleString()} tokens</p>
            </div>}
        {canManage && <button onClick={generate} disabled={generating || limitReached} className="btn btn-sm btn-primary btn-outline rounded-lg gap-1.5">
            {generating ? <span className="loading loading-spinner loading-sm"></span> : <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
              </svg>}
            {generating ? "Analyzing..." : "Analyze Now"}
          </button>}
        </div>
      </div>

      {/* Token limit banner */}
      {limitReached && usage && <div className="flex items-start gap-2.5 rounded-xl bg-warning/10 border border-warning/20 px-4 py-3 mb-4">
          <svg className="h-4 w-4 mt-0.5 shrink-0 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <div>
            <p className="text-xs font-medium text-warning">Monthly AI token limit reached</p>
            <p className="text-[11px] text-base-content/40 mt-0.5">{usage.used.toLocaleString()} / {usage.limit.toLocaleString()} tokens used. AI Insights and Provi chat are paused until next month.</p>
          </div>
        </div>}

      {/* Content */}
      {insights.length === 0 ? <div className="rounded-lg border border-white/5 bg-base-200/30 px-4 py-6 text-center">
          <p className="text-sm text-base-content/30">No insights yet.</p>
          <p className="text-xs text-base-content/20 mt-1">Click &quot;Analyze Now&quot; or wait for the next scheduled analysis. Available on Pro plans and above.</p>
        </div> : <div className="space-y-2">
          {sortedCategories.map(cat_0 => {
        const config = CATEGORY_CONFIG[cat_0] || CATEGORY_CONFIG.general;
        const items = grouped[cat_0];
        const highestSeverity = items.reduce((max, i_2) => {
          return (severityOrder[i_2.severity] ?? 4) < (severityOrder[max] ?? 4) ? i_2.severity : max;
        }, "info");
        return <div key={cat_0} className="collapse collapse-arrow rounded-lg border border-white/5 bg-base-200/20">
                <input type="checkbox" defaultChecked={highestSeverity !== "info"} />
                <div className="collapse-title py-3 min-h-0">
                  <div className="flex items-center gap-2.5">
                    <span className="text-base">{config.icon}</span>
                    <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                    <span className="text-[10px] text-base-content/20">{items.length} insight{items.length !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="collapse-content px-4 pb-3">
                  <div className="space-y-1.5">
                    {items.map(insight_0 => {
                const style = SEVERITY_STYLES[insight_0.severity] || SEVERITY_STYLES.info;
                return <div key={insight_0.insightId} className={`group rounded-lg border px-3 py-2.5 ${style.bg}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-2 min-w-0">
                              <span className="text-xs mt-0.5">{TYPE_ICONS[insight_0.type] || "ℹ️"}</span>
                              <div className="min-w-0">
                                <p className={`text-xs font-medium ${style.text}`}>{insight_0.title}</p>
                                <p className="text-[11px] text-base-content/40 mt-0.5 leading-relaxed">{insight_0.body}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-[9px] text-base-content/15">{timeAgo(insight_0.createdAt)}</span>
                              <button onClick={() => dismiss(insight_0.insightId)} className="rounded p-0.5 text-base-content/10 opacity-0 transition group-hover:opacity-100 hover:text-base-content/40" title="Dismiss">
                                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>;
              })}
                  </div>
                </div>
              </div>;
      })}
        </div>}
    </div>;
}